/**
 * Metrics § Top posts (§8.7, PRD Metrics §6; redesign § Top posts).
 *
 * Refined ranked list: large grey rank numeral, 2-line preview clamp, muted author/date meta, the
 * metric value right-aligned with the multiplier as muted text. The selector drives the
 * `topMetric` query param, so the ranking is always the server's. Rows are plain `PostRowDTO`s;
 * clicking one opens the post preview with this list as its prev/next sequence. The multiplier
 * keeps the dense-grid affordance rule: the info trigger appears only when the baseline is
 * `insufficient` (full tooltips live in the preview) — the metric column is a sibling of the row
 * button so that trigger is never a nested button.
 */

import { METRIC_KEYS, type MetricKey, type TopPostsData } from '@lw/shared';
import { MultiplierBadge, Num } from '../../../components';
import { METRIC_LABEL } from '../../../lib/labels';
import { usePostPreview } from '../PreviewContext';

/** "By <metric>" — quiet dropdown for the section head, outside the island. */
export function TopPostsMetricSelect({
  value,
  onChange,
}: {
  value: MetricKey;
  onChange: (metric: MetricKey) => void;
}) {
  return (
    <label className="row">
      <span className="muted">By</span>
      <select
        className="select"
        aria-label="Top posts metric"
        value={value}
        onChange={(event) => onChange(event.target.value as MetricKey)}
      >
        {METRIC_KEYS.map((metric) => (
          <option key={metric} value={metric}>
            {METRIC_LABEL[metric]}
          </option>
        ))}
      </select>
    </label>
  );
}

export function TopPostsSection({ data }: { data: TopPostsData }) {
  const { openPreview } = usePostPreview();
  const ids = data.rows.map((row) => row.id);
  const multiplierKey = `${data.metric}X` as `${MetricKey}X`;

  return (
    <div className="card">
      <ol className="tp-list">
        {data.rows.map((row, index) => (
          <li className="tp-item" key={row.id}>
            <button type="button" className="tp-row" onClick={() => openPreview(row.id, ids)}>
              <span className="tp-rank" aria-hidden="true">
                {String(index + 1)}
              </span>
              <span className="tp-body">
                <span className="tp-text">{row.previewText}</span>
                <span className="tp-meta">
                  <span>{row.authorName}</span>
                  <span>{row.publishedAtDisplay}</span>
                </span>
              </span>
            </button>
            <span className="tp-metrics">
              <Num value={row[data.metric]} withTooltip={false} className="tp-value" />
              <MultiplierBadge
                value={row[multiplierKey]}
                withTooltip={row[multiplierKey].insufficient}
              />
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default TopPostsSection;
